import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, FolderArchive, ClipboardList } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { CARD, Label } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { downloadBlob } from "@/components/hr/hrUtils";
import { DocRow } from "./CompDocParts";
import { BTN, Sel, StatusPill, useComp } from "./compUtils";

export function CompDocs({ tick }) {
  const [rows, setRows] = useState([]);
  const [exp, setExp] = useState(false);
  useEffect(() => { api.get("/compliance/documents").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e))); }, [tick]);
  const list = exp ? rows.filter((d) => d.expired) : rows;
  return (
    <div className={`${CARD} p-4 sm:p-6`} data-testid="comp-docs">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2"><Label>Documents et attestations ({list.length})</Label>
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" data-testid="docs-expired-only" checked={exp} onChange={(e) => setExp(e.target.checked)} />Expirés uniquement</label></div>
      <p className="mb-2 text-xs text-gray-400">Ajoutez les justificatifs depuis la fiche de chaque obligation (onglet Registre). Les documents RH et Maintenance peuvent y être liés sans être dupliqués.</p>
      <div className="divide-y divide-gray-50">{list.length === 0 ? <p className="py-3 text-sm text-gray-400">Aucun document.</p> : list.map((d) => <DocRow key={d.id} d={d} />)}</div>
    </div>
  );
}

const REPORTS = [["monthly", "Rapport mensuel de conformité"], ["annual", "Rapport annuel"], ["late", "Obligations en retard"], ["expired", "Attestations expirées"],
  ["history", "Historique des vérifications"], ["nc", "Rapport des non-conformités"], ["actions", "Plan des actions correctives"], ["site", "Rapport par établissement"], ["equipment", "Rapport par équipement"]];

function Inspection() {
  const m = useComp();
  const [auth, setAuth] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => { if (auth) api.get(`/compliance/inspection/${auth}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); else setD(null); }, [auth]);
  const dl = (fmt) => downloadBlob(`/compliance/inspection/${auth}/export.${fmt}`, `controle-${auth}.${fmt}`).catch((e) => toast.error(apiErr(e)));
  return (
    <div className={`${CARD} space-y-3 p-4 sm:p-6`} data-testid="comp-inspection">
      <p className="flex items-center gap-2 font-display text-lg font-bold"><ClipboardList className="h-5 w-5 text-[#065F46]" />Préparer un contrôle</p>
      <Sel testid="insp-authority" value={auth} onChange={setAuth} options={m.authorities} empty="Choisir l'autorité" className="max-w-sm" />
      {d && <>
        <p className="text-xs text-gray-500">Familles retenues : {d.families.join(", ")} — {d.obligations.length} obligation(s), {d.documents.length} justificatif(s), {d.nc.length} non-conformité(s).</p>
        <div className="max-h-64 divide-y divide-gray-50 overflow-y-auto">{d.obligations.map((o) => <div key={o.id} className="flex items-center justify-between gap-2 py-1.5 text-sm"><span className="truncate">{o.title}</span><StatusPill ob={o} /></div>)}</div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="insp-pdf" className={BTN} onClick={() => dl("pdf")}><FileDown className="mr-1 h-4 w-4" />Sommaire PDF</Button>
          <Button data-testid="insp-xlsx" variant="outline" className="rounded-full" onClick={() => dl("xlsx")}><FileSpreadsheet className="mr-1 h-4 w-4" />Excel</Button>
          <Button data-testid="insp-zip" variant="outline" className="rounded-full" onClick={() => dl("zip")}><FolderArchive className="mr-1 h-4 w-4" />Dossier complet (ZIP)</Button>
        </div>
        <p className="text-[11px] text-gray-400">Le dossier ne contient que les justificatifs des familles concernées. Les documents RH liés ne sont jamais inclus automatiquement dans le ZIP ; aucune donnée médicale n'est exportée. Vérifiez le contenu avant transmission.</p>
      </>}
    </div>
  );
}

export function CompReports() {
  const m = useComp();
  const now = new Date();
  const [p, setP] = useState({ year: now.getFullYear(), month: now.getMonth() + 1, site_id: "", equipment_id: "", start: `${now.getFullYear()}-01-01`, end: now.toISOString().slice(0, 10) });
  const [eqs, setEqs] = useState([]);
  useEffect(() => { api.get("/maint/equipment").then((r) => setEqs(r.data.map((e) => [e.id, e.name]))).catch(() => {}); }, []);
  const dl = (k, fmt) => {
    const q = new URLSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v))).toString();
    downloadBlob(`/compliance/reports/${k}.${fmt}?${q}`, `conformite-${k}.${fmt}`).catch((e) => toast.error(apiErr(e)));
  };
  return (
    <div className="space-y-4" data-testid="comp-reports">
      <div className={`${CARD} space-y-3 p-4 sm:p-6`}>
        <Label>Paramètres</Label>
        <div className="flex flex-wrap gap-2">
          <Input data-testid="rep-year" type="number" value={p.year} onChange={(e) => setP({ ...p, year: e.target.value })} className="w-24 rounded-xl" />
          <Input data-testid="rep-month" type="number" min="1" max="12" value={p.month} onChange={(e) => setP({ ...p, month: e.target.value })} className="w-20 rounded-xl" />
          <Input data-testid="rep-start" type="date" value={p.start} onChange={(e) => setP({ ...p, start: e.target.value })} className="w-40 rounded-xl" />
          <Input data-testid="rep-end" type="date" value={p.end} onChange={(e) => setP({ ...p, end: e.target.value })} className="w-40 rounded-xl" />
          <Sel testid="rep-site" value={p.site_id} onChange={(v) => setP({ ...p, site_id: v })} options={m.sites.map((s) => [s.id, s.name])} empty="Site…" className="w-40" />
          <Sel testid="rep-equipment" value={p.equipment_id} onChange={(v) => setP({ ...p, equipment_id: v })} options={eqs} empty="Équipement…" className="w-48" />
        </div>
        <p className="text-[11px] text-gray-400">Année / mois : rapports mensuel et annuel · Dates : historique · Site et équipement : rapports dédiés.</p>
        <div className="divide-y divide-gray-50">{REPORTS.map(([k, l]) => (
          <div key={k} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
            <span className="text-sm font-medium">{l}</span>
            <div className="flex gap-2"><Button data-testid={`rep-${k}-pdf`} size="sm" className={BTN} onClick={() => dl(k, "pdf")}><FileDown className="mr-1 h-4 w-4" />PDF</Button>
              <Button data-testid={`rep-${k}-xlsx`} size="sm" variant="outline" className="rounded-full" onClick={() => dl(k, "xlsx")}><FileSpreadsheet className="mr-1 h-4 w-4" />Excel</Button></div>
          </div>))}</div>
      </div>
      <Inspection />
    </div>
  );
}
